import { useState } from 'react';
import { Check, X, Loader2, PartyPopper, MessageSquareX } from 'lucide-react';
import { Button3D } from '@/components/ui/button-3d';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { SignaturePad } from './SignaturePad';
import { triggerPushForLatestNotification } from '@/lib/pushNotifications';
import { formatDocument, onlyDigits, isValidDocument } from '@/lib/documentUtils';

interface PublicQuoteActionsProps {
  quoteOwnerId?: string;
  quoteId: string;
  quoteTitle: string;
  currentStatus: string;
  documentType?: string;
  hasBanner?: boolean;
  /**
   * Chamado após aprovar/rejeitar. Na aprovação recebe a assinatura recém
   * desenhada: a página pública roda anônima e a RLS de `quote_approvals` só
   * libera o dono do orçamento, então passar em memória é o único jeito de o
   * cliente baixar o PDF já com a própria assinatura (sem abrir policy nova).
   */
  onStatusChanged?: (approval?: PublicApproval) => void;
}

export interface PublicApproval {
  signature: string | null;
  approverName: string | null;
  approvedAt: string;
}

const getDocumentTypeLabel = (type: string) => {
  const labels: Record<string, string> = {
    'orcamento': 'Orçamento',
    'pedido': 'Pedido',
    'recibo': 'Recibo',
    'ordem_servico': 'Ordem de serviço',
    'cotacao': 'Cotação',
    'proposta': 'Proposta',
    'proposta_comercial': 'Proposta comercial',
    'fatura': 'Fatura',
    'nota_fiscal': 'Nota fiscal',
    'contrato': 'Contrato',
    'estimativa': 'Estimativa',
    'plano_trabalho': 'Plano de trabalho',
    'comprovante': 'Comprovante',
  };
  return labels[type] || 'Orçamento';
};

const getDocumentArticle = (type: string) => {
  const feminineTypes = ['proposta', 'proposta_comercial', 'cotacao', 'estimativa', 'fatura', 'nota_fiscal', 'ordem_servico'];
  return feminineTypes.includes(type) ? 'a' : 'o';
};

export function PublicQuoteActions({ 
  quoteId, 
  quoteTitle, 
  currentStatus,
  documentType = 'orcamento',
  quoteOwnerId,
  hasBanner = false,
  onStatusChanged 
}: PublicQuoteActionsProps) {
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showRejectedDialog, setShowRejectedDialog] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [signatureDataUrl, setSignatureDataUrl] = useState<string | null>(null);
  const [approverName, setApproverName] = useState('');
  const [approverDocument, setApproverDocument] = useState('');

  const documentValid = isValidDocument(approverDocument);
  const documentInvalid = documentValid === false;

  const resetApprovalFields = () => {
    setSignatureDataUrl(null);
    setApproverName('');
    setApproverDocument('');
  };

  const isAlreadyApproved = currentStatus === 'approved' || currentStatus === 'completed';
  const isAlreadyRejected = currentStatus === 'rejected';
  const documentLabel = getDocumentTypeLabel(documentType);
  const article = getDocumentArticle(documentType);

  // Os offsets moram em `src/index.css` (`--public-*-bottom`) porque as três
  // camadas fixas desta página se somam e todas precisam da área segura do
  // Android — ver o bloco "Página pública do orçamento" lá.
  const bottomPosClass = hasBanner
    ? 'bottom-[var(--public-actions-bottom-banner)]'
    : 'bottom-[var(--public-actions-bottom)]';

  const handleApprove = async () => {
    setIsProcessing(true);
    
    try {
      const { data, error } = await supabase.rpc('approve_quote_public', {
        quote_uuid: quoteId,
        // Persistir a assinatura desenhada pelo cliente (antes era descartada).
        p_signature: signatureDataUrl,
        // Identidade opcional de quem aprovou (nome + CPF/CNPJ, só dígitos).
        p_approver_name: approverName.trim() || null,
        p_approver_document: onlyDigits(approverDocument) || null,
      });

      if (error) {
        console.error('Error approving quote:', error);
        toast.error(`Erro ao aprovar ${article} ${documentLabel.toLowerCase()}. Tente novamente.`);
        return;
      }

      if (data) {
        // Trigger push notification to quote owner
        if (quoteOwnerId) {
          triggerPushForLatestNotification(quoteOwnerId);
        }
        
        setShowApproveDialog(false);
        setShowSuccessDialog(true);
        onStatusChanged?.({
          signature: signatureDataUrl,
          approverName: approverName.trim() || null,
          approvedAt: new Date().toISOString(),
        });
      } else {
        toast.warning(`Est${article === 'a' ? 'a' : 'e'} ${documentLabel.toLowerCase()} já foi aprovad${article} ou não está mais disponível.`);
      }
    } catch (err) {
      console.error('Error approving quote:', err);
      toast.error(`Erro ao aprovar ${article} ${documentLabel.toLowerCase()}. Tente novamente.`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast.warning('Por favor, informe o motivo da rejeição.');
      return;
    }

    setIsProcessing(true);
    
    try {
      const { data, error } = await supabase.rpc('reject_quote_public', {
        quote_uuid: quoteId,
        rejection_reason: rejectionReason.trim(),
      });

      if (error) {
        console.error('Error rejecting quote:', error);
        toast.error(`Erro ao rejeitar ${article} ${documentLabel.toLowerCase()}. Tente novamente.`);
        return;
      }

      if (data) {
        // Trigger push notification to quote owner
        if (quoteOwnerId) {
          triggerPushForLatestNotification(quoteOwnerId);
        }
        
        setShowRejectDialog(false);
        setShowRejectedDialog(true);
        setRejectionReason('');
        onStatusChanged?.();
      } else {
        toast.warning(`Est${article === 'a' ? 'a' : 'e'} ${documentLabel.toLowerCase()} já foi processad${article} ou não está mais disponível.`);
      }
    } catch (err) {
      console.error('Error rejecting quote:', err);
      toast.error(`Erro ao rejeitar ${article} ${documentLabel.toLowerCase()}. Tente novamente.`);
    } finally {
      setIsProcessing(false);
    }
  };

  // Already approved state
  if (isAlreadyApproved) {
    return (
      <div className={`fixed ${bottomPosClass} right-4 z-50`}>
        <div className="flex items-center gap-2 bg-emerald-500 text-white px-4 py-2 rounded-full shadow-lg">
          <Check className="h-5 w-5" />
          <span className="font-medium">Aprovado</span>
        </div>
      </div>
    );
  }

  // Already rejected state
  if (isAlreadyRejected) {
    return (
      <div className={`fixed ${bottomPosClass} right-4 z-50`}>
        <div className="flex items-center gap-2 bg-red-500 text-white px-4 py-2 rounded-full shadow-lg">
          <X className="h-5 w-5" />
          <span className="font-medium">Rejeitado</span>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Action Buttons - centered, above banner and footer */}
      <div className={`fixed ${bottomPosClass} left-0 right-0 z-50 flex justify-center gap-2 px-4`}>
        <Button3D
          color="red"
          onClick={() => setShowRejectDialog(true)}
        >
          <X className="h-5 w-5 mr-2" />
          Rejeitar
        </Button3D>
        <Button3D
          onClick={() => setShowApproveDialog(true)}
        >
          <Check className="h-5 w-5 mr-2" />
          Aprovar
        </Button3D>
      </div>

      {/* Approve Confirmation Dialog with Signature */}
      <Dialog open={showApproveDialog} onOpenChange={(open) => {
        setShowApproveDialog(open);
        if (!open) resetApprovalFields();
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirmar aprovação</DialogTitle>
            <DialogDescription>
              Você está prestes a aprovar {article} {documentLabel.toLowerCase()} <strong>"{quoteTitle}"</strong>.
            </DialogDescription>
          </DialogHeader>

          {/* Identidade de quem aprova (opcional) */}
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="approver-name" className="text-sm">Nome (opcional)</Label>
              <Input
                id="approver-name"
                placeholder="Seu nome"
                value={approverName}
                onChange={(e) => setApproverName(e.target.value)}
                disabled={isProcessing}
                maxLength={120}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="approver-document" className="text-sm">CPF/CNPJ (opcional)</Label>
              <Input
                id="approver-document"
                inputMode="numeric"
                placeholder="000.000.000-00"
                value={formatDocument(approverDocument)}
                onChange={(e) => setApproverDocument(onlyDigits(e.target.value))}
                disabled={isProcessing}
                className={documentInvalid ? 'border-red-400 focus-visible:ring-red-400' : undefined}
              />
              {documentInvalid && (
                <p className="text-xs text-red-500">Documento inválido.</p>
              )}
            </div>
          </div>

          {/* Signature Pad */}
          <div className="py-2">
            <SignaturePad 
              onSignatureChange={setSignatureDataUrl}
              disabled={isProcessing}
            />
            <p className="text-xs text-muted-foreground mt-2">
              Ao assinar, você confirma que leu e concorda com os termos deste documento.
            </p>
          </div>

          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button3D
              color="gray"
              onClick={() => {
                setShowApproveDialog(false);
                resetApprovalFields();
              }}
              disabled={isProcessing}
            >
              Cancelar
            </Button3D>
            <Button3D
              onClick={handleApprove}
              disabled={isProcessing || !signatureDataUrl || documentInvalid}
            >
              {isProcessing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Aprovando...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Confirmar aprovação
                </>
              )}
            </Button3D>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog with Reason */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rejeitar {documentLabel}</DialogTitle>
            <DialogDescription>
              Você está prestes a rejeitar {article} {documentLabel.toLowerCase()} <strong>"{quoteTitle}"</strong>.
              <br /><br />
              Por favor, informe o motivo da rejeição para que o prestador possa entender melhor sua decisão.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="rejection-reason" className="text-sm font-medium">
              Motivo da rejeição *
            </Label>
            <Textarea
              id="rejection-reason"
              placeholder="Ex: O valor está acima do orçamento, preciso de uma proposta com desconto..."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              className="mt-2 min-h-[100px]"
              maxLength={500}
            />
            <p className="text-xs text-muted-foreground mt-1">
              {rejectionReason.length}/500 caracteres
            </p>
          </div>
          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button3D
              color="gray"
              onClick={() => {
                setShowRejectDialog(false);
                setRejectionReason('');
              }}
              disabled={isProcessing}
            >
              Cancelar
            </Button3D>
            <Button3D
              color="red"
              onClick={handleReject}
              disabled={isProcessing || !rejectionReason.trim()}
            >
              {isProcessing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Rejeitando...
                </>
              ) : (
                <>
                  <X className="h-4 w-4 mr-2" />
                  Confirmar rejeição
                </>
              )}
            </Button3D>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approval Success Dialog */}
      <Dialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
        <DialogContent className="text-center">
          <DialogHeader>
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
              <PartyPopper className="h-8 w-8 text-emerald-600" />
            </div>
            <DialogTitle className="text-xl">{documentLabel} aprovad{article === 'a' ? 'a' : 'o'}!</DialogTitle>
            <DialogDescription className="text-base">
              Obrigado! O prestador foi notificado sobre sua aprovação e entrará em contato em breve.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-center">
            <Button3D onClick={() => setShowSuccessDialog(false)}>
              Entendido
            </Button3D>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rejection Confirmed Dialog */}
      <Dialog open={showRejectedDialog} onOpenChange={setShowRejectedDialog}>
        <DialogContent className="text-center">
          <DialogHeader>
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
              <MessageSquareX className="h-8 w-8 text-red-600" />
            </div>
            <DialogTitle className="text-xl">{documentLabel} rejeitad{article === 'a' ? 'a' : 'o'}</DialogTitle>
            <DialogDescription className="text-base">
              O prestador foi notificado sobre sua decisão e receberá o motivo informado.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-center">
            <Button3D onClick={() => setShowRejectedDialog(false)}>
              Entendido
            </Button3D>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
